import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaEnvelope } from 'react-icons/fa';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);

  const token = searchParams.get('token');

  const verifyEmailToken = useCallback(async () => {
    try {
      setStatus('verifying');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const result = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(result.message);
        
        // Auto login após verificação bem-sucedida
        if (result.access_token) {
          localStorage.setItem('token', result.access_token);
          localStorage.setItem('userType', 'teacher');
          
          // Redirecionar para dashboard após 3 segundos
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        }
      } else {
        setStatus('error');
        setMessage(result.error || 'Erro ao verificar email');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Erro de conexão. Tente novamente mais tarde.');
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Token de verificação não encontrado na URL');
      return;
    }

    verifyEmailToken();
  }, [token, verifyEmailToken]);

  const handleResendEmail = async () => {
    if (!resendEmail) {
      alert('Por favor, digite seu email');
      return;
    }

    try {
      setResending(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resendEmail }),
      });

      const result = await response.json();

      if (response.ok) {
        alert('Email de verificação reenviado com sucesso!');
        setResendEmail('');
      } else {
        alert(result.error || 'Erro ao reenviar email');
      }
    } catch (error) {
      alert('Erro de conexão. Tente novamente mais tarde.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            hmwkStudio
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Verificação de Email
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-6">
          {status === 'verifying' && (
            <div className="text-center">
              <FaSpinner className="mx-auto h-12 w-12 text-blue-500 animate-spin mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Verificando seu email...
              </h3>
              <p className="text-gray-600">
                Por favor, aguarde enquanto confirmamos seu email.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <FaCheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-lg font-medium text-green-800 mb-2">
                Email Verificado com Sucesso!
              </h3>
              <p className="text-gray-600 mb-4">
                {message}
              </p>
              <p className="text-sm text-gray-500">
                Você será redirecionado para o dashboard em alguns segundos...
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Ir para Dashboard Agora
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <FaTimesCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-red-800 mb-2">
                Erro na Verificação
              </h3>
              <p className="text-gray-600 mb-6">
                {message}
              </p>

              {/* Reenviar email */}
              <div className="border-t pt-6">
                <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center justify-center">
                  <FaEnvelope className="mr-2" />
                  Reenviar Email de Verificação
                </h4>
                
                <div className="space-y-4">
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="Digite seu email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  <button
                    onClick={handleResendEmail}
                    disabled={resending}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resending ? (
                      <>
                        <FaSpinner className="animate-spin mr-2" />
                        Reenviando...
                      </>
                    ) : (
                      'Reenviar Email'
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Voltar ao Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail; 